// src/pages/Login.js
import {
  LockOutlined,
  UserOutlined,
  SafetyOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Row,
  Typography,
  Alert,
  Space,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import client from "../helpers/directusClient";
import React, { useState, useContext, useEffect } from "react";
import { AppContext } from "../context/AppContext";
import { readItems, readMe } from "@directus/sdk";
import AccountsService from "../utils/accountsService";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  recordFailedAttempt,
  clearLoginAttempts,
  isAccountLocked,
  getRemainingAttempts,
  getDeviceFingerprint,
  detectSuspiciousActivity,
} from "../utils/loginSecurity";

const { Title, Text } = Typography;

// reCAPTCHA site key. The site key is public (it ships to the browser anyway),
// so we hardcode the production key as a fallback. The env var still takes
// precedence, letting builds override it (e.g. staging) without code changes.
// This guards against the build pipeline missing the gitignored .env file.
const DEFAULT_RECAPTCHA_SITE_KEY = "6LfdBHUsAAAAAOKi9nQG_KrVKV2GXiS_EO7Z-_WC";
const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY || DEFAULT_RECAPTCHA_SITE_KEY;

const LoginForm = () => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [loading, setLoading] = useState(false);
  const [lockoutInfo, setLockoutInfo] = useState({ locked: false });
  const [remainingAttempts, setRemainingAttempts] = useState(5);
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);

  const {
    setUser,
    setCompany,
    setCards,
    setMenus,
    updateAccounts,
  } = useContext(AppContext);

  // Check account lockout status on mount
  useEffect(() => {
    const lockStatus = isAccountLocked();
    setLockoutInfo(lockStatus);
    if (!lockStatus.locked) {
      setRemainingAttempts(getRemainingAttempts());
    }

    // Check for suspicious activity
    const suspiciousCheck = detectSuspiciousActivity();
    if (suspiciousCheck.suspicious) {
      setShowSecurityWarning(true);
    }
  }, []);

  // Update lockout timer
  useEffect(() => {
    if (lockoutInfo.locked) {
      const timer = setInterval(() => {
        const lockStatus = isAccountLocked();
        setLockoutInfo(lockStatus);
        if (!lockStatus.locked) {
          setRemainingAttempts(getRemainingAttempts());
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [lockoutInfo.locked]);

  const onFinish = async (values) => {
    // Check if account is locked
    const lockStatus = isAccountLocked();
    if (lockStatus.locked) {
      message.error(
        `Account temporarily locked. Please try again in ${lockStatus.remainingMinutes} minute(s).`
      );
      return;
    }

    setLoading(true);

    try {
      // Execute reCAPTCHA v3
      if (!executeRecaptcha) {
        message.error("reCAPTCHA not loaded. Please refresh the page.");
        setLoading(false);
        return;
      }

      // Execute reCAPTCHA and get device fingerprint for security logging
      await executeRecaptcha("login");
      getDeviceFingerprint();

      // Logs in
      const user = await client.login(values.email, values.password);

      // Retrieve user details
      const me = await client.request(
        readMe({
          fields: [
            "id, email, home_cards, first_name, last_name, role.name, needs_password_change, company.id, company.plan, company.company_name, company.signup_date, company.plan_enddate, company.balance, company.sender_id, company.logo, company.skin, company.daily_disbursement_limit, company.daily_disbursement_warn_pct, company.branches.id,company.branches.branch_name",
          ],
          deep: {
            company: {
              filter: {
                status: {
                  _eq: "published",
                },
              },
            },
          },
        })
      );

      if (!me.company) {
        // message.error("No active company found for this user");
        console.log("No active company found for this user");
        setLoading(false);
        return;
      }

      const companyData = {
        id: me.company?.id,
        company_name: me.company?.company_name,
        skin: me.company?.skin,
        signup_date: me.company?.signup_date,
        branches: me.company?.branches || [],
        balance: me.company?.balance || 0,
        plan_enddate: me.company?.plan_enddate,
        plan: me.company?.plan,
        daily_disbursement_limit: me.company?.daily_disbursement_limit || 0,
        daily_disbursement_warn_pct:
          me.company?.daily_disbursement_warn_pct || 80,
        sender_id: me.company.sender_id || "Kwachaplus",
        logo: me.company?.logo || "",
      };

      setCompany(companyData);

      setUser({
        user_id: me.id,
        name: `${me.first_name} ${me.last_name}`,
        first_name: me.first_name,
        last_name: me.last_name,
        email: me.email,
        role: me.role.name,
        needs_password_change: me.needs_password_change || false,
      });

      // Fetch accounts after setting company
      try {
        const accountData = await AccountsService.fetchAccounts(
          companyData.id,
          user.access_token
        );
        updateAccounts(accountData);
      } catch (accountError) {
        console.error("Error fetching accounts during login:", accountError);
        // Don't fail login if accounts fetch fails, just log the error
      }

      const menus = await client.request(
        readItems("companies", {
          fields: ["menus.menus_id.*.*"],
          filter: {
            id: {
              _eq: companyData.id,
            },
          },
        })
      );
      //console.log("Menus:", menus);

      const cards = await client.request(
        readItems("home_cards", {
          fields: ["*.*"],
        })
      );

      setCards(cards);
      // Extract menus from the company response, flatten the structure, and sort by position
      const companyMenus =
        menus.length > 0
          ? menus[0].menus
              .map((menu) => menu.menus_id)
              .sort((a, b) => a.position - b.position)
          : [];
      setMenus(companyMenus);

      setLoading(false);

      const expirationTime = Date.now() + user.expires;
      localStorage.setItem("isAuth", "true");
      localStorage.setItem("userRole", me.role.name);
      localStorage.setItem("token", user.access_token);
      localStorage.setItem("expires", expirationTime);

      // Clear login attempts on successful login
      clearLoginAttempts();

      message.success("Login successful! Redirecting...");
      navigate("/dashboard");
    } catch (error) {
      console.log("error", error);

      // Record failed login attempt
      const attemptResult = recordFailedAttempt();

      if (attemptResult.locked) {
        message.error(
          `Too many failed attempts. Account locked for 15 minutes.`
        );
        setLockoutInfo({ locked: true, remainingMinutes: 15 });
      } else {
        const remaining = attemptResult.remainingAttempts;
        message.error(
          `Login failed. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining before account lockout.`
        );
        setRemainingAttempts(remaining);
      }

      setLoading(false);
    }
  };

  return (
    <Row
      justify="center"
      align="middle"
      style={{
        height: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "0 15px",
      }}
    >
      <Col xs={24} sm={20} md={16} lg={12} xl={8}>
        <Card
          hoverable
          style={{
            borderRadius: 12,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
          }}
          bodyStyle={{
            padding: "40px 32px",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <img
              src="/images/logo.png"
              alt="KwachaPlus"
              style={{
                height: 96,
                maxWidth: "100%",
                marginBottom: 24,
                borderRadius: 8,
              }}
            />
            <Title level={2} style={{ marginBottom: 8, color: "#2D3748" }}>
              Welcome Back
            </Title>
            <Text type="secondary" style={{ fontSize: 14 }}>
              Please sign in to continue
            </Text>
          </div>

          {/* Security Alerts */}
          {lockoutInfo.locked && (
            <Alert
              message="Account Temporarily Locked"
              description={`Too many failed login attempts. Please try again in ${lockoutInfo.remainingMinutes} minute(s).`}
              type="error"
              icon={<LockOutlined />}
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          {showSecurityWarning && !lockoutInfo.locked && (
            <Alert
              message="Security Notice"
              description="Multiple rapid login attempts detected. Please proceed carefully."
              type="warning"
              icon={<WarningOutlined />}
              showIcon
              closable
              onClose={() => setShowSecurityWarning(false)}
              style={{ marginBottom: 24 }}
            />
          )}

          {!lockoutInfo.locked && remainingAttempts <= 3 && remainingAttempts > 0 && (
            <Alert
              message={`${remainingAttempts} login attempt${remainingAttempts !== 1 ? 's' : ''} remaining`}
              description="Your account will be temporarily locked after too many failed attempts."
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          <Form
            name="loginForm"
            layout="vertical"
            autoComplete="off"
            onFinish={onFinish}
            initialValues={{ remember: true }}
          >
            <Form.Item
              label="Email"
              name="email"
              rules={[{ required: true, message: "Please enter your email." }]}
              style={{ marginBottom: 24 }}
            >
              <Input
                autoComplete="off"
                prefix={<UserOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                placeholder="Enter email"
                size="large"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              autoComplete="off"
              rules={[
                { required: true, message: "Please enter your password." },
              ]}
              style={{ marginBottom: 24 }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                placeholder="Enter password"
                size="large"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={loading}
                disabled={lockoutInfo.locked}
                style={{
                  height: 48,
                  borderRadius: 8,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  fontWeight: 600,
                }}
              >
                {lockoutInfo.locked ? "Account Locked" : "Sign In"}
              </Button>
            </Form.Item>

            {/* reCAPTCHA Badge Info */}
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <Space>
                <SafetyOutlined style={{ color: "#52c41a" }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Protected by reCAPTCHA
                </Text>
              </Space>
            </div>

            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <Link
                to="/forgot-password"
                style={{
                  color: "#667eea",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Forgot Password?
              </Link>
            </div>
            {/* 
            <Divider style={{ color: "#A0AEC0", fontSize: 12 }}>
              OR CONTINUE WITH
            </Divider> */}

            {/* <Row gutter={16} style={{ marginTop: 24 }}>
              <Col span={12}>
                <Button
                  block
                  size="large"
                  style={{
                    height: 44,
                    borderRadius: 8,
                    background: "#F8F9FA",
                    color: "#2D3748",
                    fontWeight: 500,
                  }}
                  icon={<GoogleOutlined />}
                >
                  Google
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  block
                  size="large"
                  style={{
                    height: 44,
                    borderRadius: 8,
                    background: "#F8F9FA",
                    color: "#2D3748",
                    fontWeight: 500,
                  }}
                  icon={<FacebookOutlined />}
                >
                  Facebook
                </Button>
              </Col>
            </Row> */}

            {/* <div style={{ textAlign: "center", marginTop: 32 }}>
              <Text type="secondary" style={{ fontSize: 14 }}>
                Don't have an account?{" "}
                <Link
                  to={"/signup"}
                  style={{
                    color: "#667eea",
                    fontWeight: 500,
                  }}
                >
                  Create Account
                </Link>
              </Text>
            </div> */}
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

// Wrap the login form with reCAPTCHA provider
const Login = () => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <LoginForm />
    </GoogleReCaptchaProvider>
  );
};

export default Login;
